import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Plus, Edit, Trash2, Save, Upload, X } from 'lucide-react';
import { ImageField } from '../components/admin/ImageField';
import { GalleryEditor } from '../components/admin/GalleryEditor';
import { CONFIG_GROUPS, CONFIG_SECTIONS } from '../components/admin/configSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminPanel() {
  const [products, setProducts] = useState([]);
  const [customIdeas, setCustomIdeas] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('site');
  const [siteGroup, setSiteGroup] = useState('general');
  const { config, updateConfig, fetchConfig } = useSiteConfig();
  const [configForm, setConfigForm] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_order_amount: 0,
    max_uses: '',
    expires_at: '',
    active: true
  });
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: 0,
    category: 'Decoración',
    images: [''],
    stock: 100
  });

  useEffect(() => {
    if (config) {
      setConfigForm(config);
    }
  }, [config]);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'ideas') {
      fetchIdeas();
    } else if (activeTab === 'orders') {
      fetchOrders();
    } else if (activeTab === 'coupons') {
      fetchCoupons();
    }
  }, [activeTab]);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/coupons`, { withCredentials: true });
      setCoupons(data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async () => {
    if (!couponForm.code.trim()) {
      toast.error('Código requerido');
      return;
    }
    try {
      const payload = {
        code: couponForm.code.trim().toUpperCase(),
        discount_type: couponForm.discount_type,
        discount_value: parseFloat(couponForm.discount_value) || 0,
        min_order_amount: parseFloat(couponForm.min_order_amount) || 0,
        max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
        expires_at: couponForm.expires_at || null,
        active: couponForm.active
      };
      await axios.post(`${API_URL}/api/coupons`, payload, { withCredentials: true });
      toast.success('Cupón creado');
      setShowCouponModal(false);
      setCouponForm({ code: '', discount_type: 'percentage', discount_value: 10, min_order_amount: 0, max_uses: '', expires_at: '', active: true });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cupón');
    }
  };

  const handleToggleCoupon = async (code, active) => {
    try {
      await axios.put(`${API_URL}/api/coupons/${code}`, { active: !active }, { withCredentials: true });
      fetchCoupons();
    } catch {
      toast.error('Error al actualizar cupón');
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm(`¿Eliminar cupón ${code}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/coupons/${code}`, { withCredentials: true });
      toast.success('Cupón eliminado');
      fetchCoupons();
    } catch {
      toast.error('Error al eliminar cupón');
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/products`);
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIdeas = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/custom-ideas`, {
        withCredentials: true
      });
      setCustomIdeas(data);
    } catch (error) {
      console.error('Error fetching ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/orders/admin/all`, {
        withCredentials: true
      });
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIdea = async (ideaId) => {
    if (!window.confirm('¿Eliminar esta idea?')) return;
    try {
      await axios.delete(`${API_URL}/api/custom-ideas/${ideaId}`, {
        withCredentials: true
      });
      toast.success('Idea eliminada');
      fetchIdeas();
    } catch (error) {
      toast.error('Error al eliminar idea');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/orders/admin/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Estado actualizado');
      fetchOrders();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('¿Eliminar este pedido?')) return;
    try {
      await axios.delete(`${API_URL}/api/orders/admin/${orderId}`, {
        withCredentials: true
      });
      toast.success('Pedido eliminado');
      fetchOrders();
    } catch (error) {
      toast.error('Error al eliminar pedido');
    }
  };

  const handleConfigChange = (field, value) => {
    setConfigForm({ ...configForm, [field]: value });
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    const result = await updateConfig(configForm);
    if (result.success) {
      toast.success('Configuración guardada exitosamente');
    } else {
      toast.error('Error al guardar: ' + result.error);
    }
    setSavingConfig(false);
  };

  const handleProductImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.info('Subiendo imagen...');
      const { data } = await axios.post(`${API_URL}/api/admin/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProductForm({ ...productForm, images: [data.url] });
      toast.success('Imagen subida');
    } catch (error) {
      toast.error('Error al subir imagen');
    }
  };

  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category,
        images: product.images || [''],
        stock: product.stock || 100
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        description: '',
        price: 0,
        category: 'Decoración',
        images: [''],
        stock: 100
      });
    }
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    try {
      if (editingProduct) {
        await axios.put(
          `${API_URL}/api/products/${editingProduct.product_id}`,
          productForm,
          { withCredentials: true }
        );
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API_URL}/api/products`, productForm, {
          withCredentials: true
        });
        toast.success('Producto creado');
      }
      setShowProductModal(false);
      fetchProducts();
    } catch (error) {
      toast.error('Error al guardar producto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, {
        withCredentials: true
      });
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const categories = [
    'Llaveros',
    'Lectura',
    'Imanes',
    'Mini cosas',
    'Accesorios',
    'Decoración'
  ];

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-light text-[#374963] mb-8" data-testid="admin-panel-title">
          Panel de Administración
        </h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('site')}
            className={`px-6 py-3 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'site'
                ? 'bg-[#374963] text-white'
                : 'bg-white text-[#73706e] hover:bg-[#374963] hover:text-white'
            } shadow-[0_4px_16px_rgba(0,0,0,0.04)]`}
            data-testid="admin-tab-site"
          >
            Editar Sitio Web
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-3 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'products'
                ? 'bg-[#374963] text-white'
                : 'bg-white text-[#73706e] hover:bg-[#374963] hover:text-white'
            } shadow-[0_4px_16px_rgba(0,0,0,0.04)]`}
            data-testid="admin-tab-products"
          >
            Productos
          </button>
          <button
            onClick={() => setActiveTab('ideas')}
            className={`px-6 py-3 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'ideas'
                ? 'bg-[#374963] text-white'
                : 'bg-white text-[#73706e] hover:bg-[#374963] hover:text-white'
            } shadow-[0_4px_16px_rgba(0,0,0,0.04)]`}
            data-testid="admin-tab-ideas"
          >
            Ideas de Clientes
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'orders'
                ? 'bg-[#374963] text-white'
                : 'bg-white text-[#73706e] hover:bg-[#374963] hover:text-white'
            } shadow-[0_4px_16px_rgba(0,0,0,0.04)]`}
            data-testid="admin-tab-orders"
          >
            Pedidos
          </button>
          <button
            onClick={() => setActiveTab('coupons')}
            className={`px-6 py-3 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'coupons'
                ? 'bg-[#374963] text-white'
                : 'bg-white text-[#73706e] hover:bg-[#374963] hover:text-white'
            } shadow-[0_4px_16px_rgba(0,0,0,0.04)]`}
            data-testid="admin-tab-coupons"
          >
            Cupones
          </button>
        </div>

        {/* Site Editor Tab */}
        {activeTab === 'site' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-6 sticky top-20 z-30">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-[#374963]">Configuración del Sitio</h2>
                <button
                  onClick={handleSaveConfig}
                  disabled={savingConfig}
                  className="px-6 py-3 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors disabled:opacity-50 flex items-center space-x-2"
                  data-testid="save-config-button"
                >
                  <Save className="w-5 h-5" />
                  <span>{savingConfig ? 'Guardando...' : 'Guardar Todos los Cambios'}</span>
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2" data-testid="site-group-nav">
              {CONFIG_GROUPS.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setSiteGroup(g.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${siteGroup === g.id ? 'bg-[#111] text-white' : 'bg-white text-[#5f6672] hover:text-[#111]'}`}
                  data-testid={`site-group-${g.id}`}
                >
                  {g.label}
                </button>
              ))}
            </div>

            {siteGroup === 'gallery' && (
              <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-8" data-testid="config-section-gallery-items">
                <h3 className="text-xl font-semibold text-[#111] mb-6 border-b border-[rgba(17,17,17,0.08)] pb-3">Fotos de la Galería</h3>
                <GalleryEditor items={configForm.gallery_items || []} onChange={(v) => handleConfigChange('gallery_items', v)} />
              </div>
            )}

            {CONFIG_SECTIONS.filter((s) => s.group === siteGroup).map((section, idx) => (
              <div key={section.title} className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-8" data-testid={`config-section-${idx}`}>
                <h3 className="text-xl font-semibold text-[#374963] mb-6 border-b border-[rgba(55,73,99,0.1)] pb-3">
                  {section.title}
                </h3>
                <div className="space-y-6">
                  {section.fields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-[#374963] mb-2">
                        {field.label}
                      </label>
                      {field.type === 'text' && (
                        <input
                          type="text"
                          value={configForm[field.key] || ''}
                          onChange={(e) => handleConfigChange(field.key, e.target.value)}
                          className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                          data-testid={`config-input-${field.key}`}
                        />
                      )}
                      {field.type === 'textarea' && (
                        <textarea
                          value={configForm[field.key] || ''}
                          onChange={(e) => handleConfigChange(field.key, e.target.value)}
                          rows={4}
                          className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                          data-testid={`config-textarea-${field.key}`}
                        />
                      )}
                      {field.type === 'image' && (
                        <ImageField value={configForm[field.key]} onChange={(v) => handleConfigChange(field.key, v)} testId={field.key} />
                      )}
                      {field.type === 'checkbox' && (
                        <label className="inline-flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!configForm[field.key]}
                            onChange={(e) => handleConfigChange(field.key, e.target.checked)}
                            className="w-5 h-5 accent-[#374963]"
                            data-testid={`config-checkbox-${field.key}`}
                          />
                          <span className="text-sm text-[#73706e]">
                            {configForm[field.key] ? 'Activado' : 'Desactivado'}
                          </span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-[#374963]">Productos</h2>
              <button
                onClick={() => openProductModal()}
                className="px-4 py-2 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors flex items-center space-x-2"
                data-testid="add-product-button"
              >
                <Plus className="w-5 h-5" />
                <span>Añadir Producto</span>
              </button>
            </div>

            {loading ? (
              <p className="text-[#73706e]">Cargando productos...</p>
            ) : products.length === 0 ? (
              <p className="text-[#73706e]">No hay productos</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[rgba(55,73,99,0.1)]">
                      <th className="text-left py-3 px-4 text-[#374963]">Imagen</th>
                      <th className="text-left py-3 px-4 text-[#374963]">Nombre</th>
                      <th className="text-left py-3 px-4 text-[#374963]">Categoría</th>
                      <th className="text-left py-3 px-4 text-[#374963]">Precio</th>
                      <th className="text-left py-3 px-4 text-[#374963]">Stock</th>
                      <th className="text-left py-3 px-4 text-[#374963]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.product_id} className="border-b border-[rgba(55,73,99,0.1)]" data-testid={`admin-product-${product.product_id}`}>
                        <td className="py-3 px-4">
                          <img
                            src={product.images && product.images[0] ? product.images[0] : 'https://via.placeholder.com/60'}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        </td>
                        <td className="py-3 px-4 text-[#73706e]">{product.name}</td>
                        <td className="py-3 px-4 text-[#73706e]">{product.category}</td>
                        <td className="py-3 px-4 text-[#73706e]">{product.price.toFixed(2)}€</td>
                        <td className="py-3 px-4 text-[#73706e]">{product.stock}</td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openProductModal(product)}
                              className="p-2 text-[#374963] hover:text-[#785534]"
                              data-testid={`edit-product-${product.product_id}`}
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.product_id)}
                              className="p-2 text-red-500 hover:text-red-700"
                              data-testid={`delete-product-${product.product_id}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Ideas Tab */}
        {activeTab === 'ideas' && (
          <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-8">
            <h2 className="text-2xl font-semibold text-[#374963] mb-6">Ideas de Clientes</h2>
            {loading ? (
              <p className="text-[#73706e]">Cargando ideas...</p>
            ) : customIdeas.length === 0 ? (
              <p className="text-[#73706e]">No hay ideas enviadas</p>
            ) : (
              <div className="space-y-4">
                {customIdeas.map((idea) => (
                  <div key={idea.id} className="border border-[rgba(55,73,99,0.1)] rounded-lg p-6" data-testid={`admin-idea-${idea.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-semibold text-[#374963]">{idea.name}</p>
                        <p className="text-sm text-[#73706e]">{idea.email}</p>
                        {idea.phone && <p className="text-sm text-[#73706e]">{idea.phone}</p>}
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 bg-[#785534] text-white rounded-full text-sm">
                          {idea.status}
                        </span>
                        <button
                          onClick={() => handleDeleteIdea(idea.id)}
                          className="p-2 text-red-500 hover:text-red-700"
                          data-testid={`delete-idea-${idea.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-[#73706e] mb-2">{idea.description}</p>
                    {idea.budget && <p className="text-sm text-[#73706e]"><strong>Presupuesto:</strong> {idea.budget}</p>}
                    {idea.needed_date && <p className="text-sm text-[#73706e]"><strong>Fecha:</strong> {idea.needed_date}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-8">
            <h2 className="text-2xl font-semibold text-[#374963] mb-6">Pedidos Recibidos</h2>
            {loading ? (
              <p className="text-[#73706e]">Cargando pedidos...</p>
            ) : orders.length === 0 ? (
              <p className="text-[#73706e]">No hay pedidos aún</p>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.order_id} className="border border-[rgba(55,73,99,0.1)] rounded-lg p-6" data-testid={`admin-order-${order.order_id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-semibold text-[#374963] text-lg">Pedido #{order.order_id.substring(0, 8).toUpperCase()}</p>
                        <p className="text-sm text-[#73706e]">
                          {new Date(order.created_at).toLocaleString('es-ES')}
                        </p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <select
                          value={order.status}
                          onChange={(e) => handleUpdateOrderStatus(order.order_id, e.target.value)}
                          className="px-3 py-2 border border-[rgba(55,73,99,0.2)] rounded-lg text-sm"
                          data-testid={`order-status-${order.order_id}`}
                        >
                          <option value="pending">Pendiente</option>
                          <option value="paid">Pagado</option>
                          <option value="processing">En producción</option>
                          <option value="shipped">Enviado</option>
                          <option value="delivered">Entregado</option>
                          <option value="cancelled">Cancelado</option>
                        </select>
                        <button
                          onClick={() => handleDeleteOrder(order.order_id)}
                          className="p-2 text-red-500 hover:text-red-700"
                          data-testid={`delete-order-${order.order_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-semibold text-[#374963] mb-1">Cliente:</p>
                        <p className="text-sm text-[#73706e]">{order.customer_name}</p>
                        <p className="text-sm text-[#73706e]">{order.customer_email}</p>
                        {order.customer_phone && <p className="text-sm text-[#73706e]">{order.customer_phone}</p>}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#374963] mb-1">Envío:</p>
                        <p className="text-sm text-[#73706e]">{order.shipping_address}</p>
                        <p className="text-sm text-[#73706e]">{order.postal_code} {order.city}</p>
                      </div>
                    </div>

                    <div className="border-t border-[rgba(55,73,99,0.1)] pt-4">
                      <p className="text-sm font-semibold text-[#374963] mb-2">Productos:</p>
                      <div className="space-y-1">
                        {order.items && order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-[#73706e]">{item.name} x {item.quantity}</span>
                            <span className="text-[#374963] font-semibold">{item.total ? item.total.toFixed(2) : (item.unit_price * item.quantity).toFixed(2)}€</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-[rgba(55,73,99,0.1)] pt-4 mt-4 space-y-1 text-sm">
                      {order.subtotal !== undefined && (
                        <div className="flex justify-between text-[#73706e]">
                          <span>Subtotal:</span>
                          <span>{order.subtotal.toFixed(2)}€</span>
                        </div>
                      )}
                      {order.shipping !== undefined && (
                        <div className="flex justify-between text-[#73706e]">
                          <span>Portes:</span>
                          <span>{order.shipping === 0 ? 'GRATIS' : `${order.shipping.toFixed(2)}€`}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-semibold text-[#374963] pt-2 border-t border-[rgba(55,73,99,0.1)]">
                        <span>Total:</span>
                        <span>{order.total.toFixed(2)}€</span>
                      </div>
                    </div>

                    {order.notes && (
                      <div className="mt-4 p-3 bg-[#FAFAFA] rounded-lg">
                        <p className="text-sm text-[#73706e]"><strong>Notas:</strong> {order.notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-8" data-testid="coupons-tab">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-[#374963]">Cupones de Descuento</h2>
              <button
                onClick={() => setShowCouponModal(true)}
                className="px-5 py-3 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors flex items-center gap-2"
                data-testid="new-coupon-button"
              >
                <Plus className="w-5 h-5" />
                <span>Nuevo Cupón</span>
              </button>
            </div>

            {loading ? (
              <p className="text-[#73706e]">Cargando...</p>
            ) : coupons.length === 0 ? (
              <p className="text-[#73706e] text-center py-12">No hay cupones creados todavía. Crea el primero.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[#73706e] border-b border-[rgba(55,73,99,0.1)]">
                    <tr>
                      <th className="py-3 px-2">Código</th>
                      <th className="py-3 px-2">Tipo</th>
                      <th className="py-3 px-2">Valor</th>
                      <th className="py-3 px-2">Pedido mín.</th>
                      <th className="py-3 px-2">Usos</th>
                      <th className="py-3 px-2">Caduca</th>
                      <th className="py-3 px-2">Estado</th>
                      <th className="py-3 px-2">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((c) => (
                      <tr key={c.code} className="border-b border-[rgba(55,73,99,0.05)]" data-testid={`coupon-row-${c.code}`}>
                        <td className="py-3 px-2 font-mono font-semibold text-[#374963]">{c.code}</td>
                        <td className="py-3 px-2">
                          {c.discount_type === 'percentage' && 'Porcentaje'}
                          {c.discount_type === 'fixed' && 'Importe fijo'}
                          {c.discount_type === 'free_shipping' && 'Envío gratis'}
                        </td>
                        <td className="py-3 px-2">
                          {c.discount_type === 'percentage' && `${c.discount_value}%`}
                          {c.discount_type === 'fixed' && `${c.discount_value}€`}
                          {c.discount_type === 'free_shipping' && '—'}
                        </td>
                        <td className="py-3 px-2">{c.min_order_amount ? `${c.min_order_amount}€` : '—'}</td>
                        <td className="py-3 px-2">
                          {c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}
                        </td>
                        <td className="py-3 px-2">
                          {c.expires_at ? new Date(c.expires_at).toLocaleDateString('es-ES') : '—'}
                        </td>
                        <td className="py-3 px-2">
                          <button
                            onClick={() => handleToggleCoupon(c.code, c.active)}
                            className={`px-3 py-1 rounded-full text-xs font-medium ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}
                            data-testid={`toggle-coupon-${c.code}`}
                          >
                            {c.active ? 'Activo' : 'Inactivo'}
                          </button>
                        </td>
                        <td className="py-3 px-2">
                          <button
                            onClick={() => handleDeleteCoupon(c.code)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-coupon-${c.code}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Coupon Modal */}
        {showCouponModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="coupon-modal">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white p-6 border-b border-[rgba(55,73,99,0.1)] flex justify-between items-center">
                <h3 className="text-2xl font-semibold text-[#374963]">Nuevo Cupón</h3>
                <button onClick={() => setShowCouponModal(false)} data-testid="close-coupon-modal">
                  <X className="w-6 h-6 text-[#73706e]" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-1">Código</label>
                  <input
                    type="text"
                    value={couponForm.code}
                    onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                    placeholder="VERANO10"
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963] uppercase font-mono"
                    data-testid="coupon-form-code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-1">Tipo de descuento</label>
                  <select
                    value={couponForm.discount_type}
                    onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })}
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="coupon-form-type"
                  >
                    <option value="percentage">Porcentaje (%)</option>
                    <option value="fixed">Importe fijo (€)</option>
                    <option value="free_shipping">Envío gratis</option>
                  </select>
                </div>
                {couponForm.discount_type !== 'free_shipping' && (
                  <div>
                    <label className="block text-sm font-medium text-[#374963] mb-1">
                      Valor {couponForm.discount_type === 'percentage' ? '(%)' : '(€)'}
                    </label>
                    <input
                      type="number"
                      value={couponForm.discount_value}
                      onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })}
                      className="w-full px-4 py-3 border border-[rgba(55,73,99,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                      data-testid="coupon-form-value"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-1">Pedido mínimo (€) — opcional</label>
                  <input
                    type="number"
                    value={couponForm.min_order_amount}
                    onChange={(e) => setCouponForm({ ...couponForm, min_order_amount: e.target.value })}
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="coupon-form-min"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-1">Usos máximos — opcional</label>
                  <input
                    type="number"
                    value={couponForm.max_uses}
                    onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })}
                    placeholder="Dejar vacío = ilimitado"
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="coupon-form-max"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-1">Fecha de caducidad — opcional</label>
                  <input
                    type="date"
                    value={couponForm.expires_at}
                    onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })}
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="coupon-form-expires"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowCouponModal(false)}
                    className="flex-1 px-4 py-3 border border-[rgba(55,73,99,0.15)] rounded-lg hover:bg-gray-50 transition-colors"
                    data-testid="cancel-coupon-button"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreateCoupon}
                    className="flex-1 px-4 py-3 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors"
                    data-testid="save-coupon-button"
                  >
                    Crear
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product Modal */}
        {showProductModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="product-modal">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white p-6 border-b border-[rgba(55,73,99,0.1)] flex justify-between items-center">
                <h3 className="text-2xl font-semibold text-[#374963]">
                  {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
                </h3>
                <button
                  onClick={() => setShowProductModal(false)}
                  className="p-2 text-[#73706e] hover:text-[#374963]"
                  data-testid="close-product-modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-2">Nombre</label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-2">Descripción</label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="product-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#374963] mb-2">Precio (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={productForm.price}
                      onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) })}
                      className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#374963] mb-2">Stock</label>
                    <input
                      type="number"
                      value={productForm.stock}
                      onChange={(e) => setProductForm({ ...productForm, stock: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                      data-testid="product-stock-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-2">Categoría</label>
                  <select
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="product-category-select"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-2">Imagen</label>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={productForm.images[0] || ''}
                      onChange={(e) => setProductForm({ ...productForm, images: [e.target.value] })}
                      placeholder="URL de la imagen"
                      className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                      data-testid="product-image-url-input"
                    />
                    <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-[#374963] text-white rounded-lg hover:bg-[#4A5F7D] transition-colors space-x-2">
                      <Upload className="w-4 h-4" />
                      <span>Subir imagen</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProductImageUpload}
                        className="hidden"
                        data-testid="product-image-upload"
                      />
                    </label>
                    {productForm.images[0] && (
                      <img src={productForm.images[0]} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                    )}
                  </div>
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleSaveProduct}
                    className="flex-1 py-3 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors"
                    data-testid="save-product-button"
                  >
                    {editingProduct ? 'Actualizar' : 'Crear'} Producto
                  </button>
                  <button
                    onClick={() => setShowProductModal(false)}
                    className="flex-1 py-3 bg-white border border-[rgba(55,73,99,0.2)] text-[#374963] rounded-lg hover:bg-[#FAFAFA] transition-colors"
                    data-testid="cancel-product-button"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
